import { useEffect, useState, type DragEvent } from 'react';
import type { AssetDto } from '@indusketch/shared';
import { assetsApi } from '../../lib/api/endpoints.js';
import { userMessage } from '../../lib/errors.js';
import { useAuthStore } from '../auth/authStore.js';
import { MAX_UPLOAD_BYTES } from '../editor/store/assetStore.js';
import { AssetCard } from '../assets/AssetCard.js';
import { Icon } from '../../ui/Icon.js';
import { currentI18n, useI18n } from '../../i18n/i18n.js';

/**
 * Asks before deleting a model and names the projects that still use it; they keep working
 * and show a placeholder box instead.
 */
export async function confirmAssetDelete(asset: Pick<AssetDto, 'id' | 'name'>): Promise<boolean> {
  const { m, fmt } = currentI18n();
  const { projects } = await assetsApi.usage(asset.id);
  const usedIn =
    projects.length === 0
      ? m.assets.unused
      : `${m.assets.usedIn}\n${projects.map((p) => `• ${p.name}`).join('\n')}\n\n${m.assets.placeholderAfter}`;
  return window.confirm(`${fmt(m.assets.confirmDelete, { name: asset.name })}\n\n${usedIn}`);
}

/** Checked in the browser to avoid a pointless upload; the server checks again. */
export function uploadProblem(file: File): string | null {
  const { m, fmt } = currentI18n();
  if (!/\.glb$/i.test(file.name)) return m.editor.messages.glbOnly;
  if (file.size > MAX_UPLOAD_BYTES)
    return fmt(m.editor.messages.tooLarge, { mb: MAX_UPLOAD_BYTES / 1024 / 1024 });
  return null;
}

/** Drop target for GLB files; `onFile` receives the first dropped file. */
export function useFileDrop(onFile: (file: File) => void) {
  const [over, setOver] = useState(false);
  const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes('Files');
  return {
    over,
    handlers: {
      onDragOver: (e: DragEvent) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setOver(true);
      },
      onDragLeave: (e: DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      },
      onDrop: (e: DragEvent) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        setOver(false);
        const file = e.dataTransfer.files[0];
        if (file) onFile(file);
      },
    },
  };
}

/** The user's own uploaded models (system library entries are managed by admins). */
export function AssetManager() {
  const { m } = useI18n();
  const user = useAuthStore((s) => s.user);
  const [assets, setAssets] = useState<AssetDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = () =>
    assetsApi
      .list()
      .then((r) => setAssets(r.assets.filter((a) => a.ownerId === user?.id)))
      .catch((err: unknown) => setError(userMessage(err)));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per user
  }, [user?.id]);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(userMessage(err));
    }
  };

  const upload = (file: File) => {
    const problem = uploadProblem(file);
    if (problem) {
      setError(problem);
      return;
    }
    setUploading(true);
    void act(() => assetsApi.upload(file)).finally(() => setUploading(false));
  };
  const drop = useFileDrop(upload);

  return (
    <section
      className={`card block-card${drop.over ? ' is-drop-target' : ''}`}
      aria-label={m.assets.title}
      data-testid="asset-manager"
      {...drop.handlers}
    >
      <header className="block-head">
        <h2 className="block-title">
          {m.assets.title}
          {assets && assets.length > 0 && <span className="count-badge">{assets.length}</span>}
        </h2>
        <label className={`btn${uploading ? ' is-disabled' : ''}`}>
          <Icon name="upload" />
          {uploading ? m.assets.uploading : m.assets.upload}
          <input
            type="file"
            accept=".glb,model/gltf-binary"
            hidden
            disabled={uploading}
            data-testid="asset-upload"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) upload(file);
            }}
          />
        </label>
      </header>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {assets?.length === 0 && (
        <div className="empty-state">
          <Icon name="box" size={28} />
          <p className="muted">{m.assets.empty}</p>
        </div>
      )}
      {assets && assets.length > 0 && (
        <div className="tile-grid asset-grid">
          {assets.map((a) => (
            <AssetCard
              key={a.id}
              asset={a}
              actions={[
                {
                  label: m.common.renameAction,
                  icon: 'pen',
                  onSelect: () => {
                    const name = window.prompt(m.assets.newName, a.name);
                    if (name?.trim()) void act(() => assetsApi.rename(a.id, name.trim()));
                  },
                },
                {
                  label: m.common.delete,
                  icon: 'trash',
                  danger: true,
                  onSelect: () =>
                    void act(async () => {
                      if (await confirmAssetDelete(a)) await assetsApi.remove(a.id);
                    }),
                },
              ]}
            />
          ))}
        </div>
      )}
      {drop.over && <div className="drop-hint">{m.assets.dropHint}</div>}
    </section>
  );
}
